// client/src/components/admin/UserManagement.js

import React, { useEffect, useState } from 'react';
import axiosInstance from '../../axiosInstance';
import { Typography, Table, TableBody, TableCell, TableHead, TableRow, Switch, IconButton, Tooltip, Alert, Box, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import AppBarHeader from '../AppBarHeader';
import { jwtDecode } from 'jwt-decode';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = token ? jwtDecode(token) : null;
  const userId = user ? user.id : null;
  const isAdmin = user ? user.isAdmin : false;

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use admin-only endpoint to include disabled users
      const response = await axiosInstance.get('/admin/users/full', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Access denied or an error occurred.');
      if (error.response && error.response.status === 403) {
        navigate('/dashboard');
      }
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, [isAdmin, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleRoleChange = async (userId, isAdmin) => {
    try {
      const token = localStorage.getItem('token');
      await axiosInstance.put(
        `/admin/users/${userId}/role`,
        { isAdmin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      setError('Failed to update user role.');
    }
  };

  const handleDisableToggle = async (userId, isDisabled) => {
    try {
      const token = localStorage.getItem('token');
      await axiosInstance.put(
        `/admin/users/${userId}/status`,
        { isDisabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
    } catch (error) {
      console.error('Error toggling disable status:', error);
      setError('Failed to toggle disable status.');
    }
  };

  const handleEmailVerifiedToggle = async (userId, emailVerified) => {
    try {
      const token = localStorage.getItem('token');
      await axiosInstance.put(
        `/admin/users/${userId}/email-verified`,
        { emailVerified },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
    } catch (error) {
      console.error('Error toggling email verified status:', error);
      setError('Failed to toggle email verified status.');
    }
  };

  const handleCanModerateToggle = async (targetUserId, canModerate) => {
    try {
      const token = localStorage.getItem('token');
      await axiosInstance.patch(
        `/admin/users/${targetUserId}/moderation`,
        { canModerate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
    } catch (error) {
      console.error('Error toggling canModerate:', error);
      setError('Failed to update moderation access.');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axiosInstance.delete(`/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user.');
    }
  };

  const navigateToDeletedUsers = () => {
    navigate('/admin/deleted-users');
  };

  if (!isAdmin) {
    return (
      <>
        <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} />
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Access denied. Admin privileges required.</Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} />
      <Box sx={{ padding: 4 }}>
        <Typography variant="h4" gutterBottom>
          User Management
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, flexWrap: 'wrap', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/create-account')}
          >
            Create New Account
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={navigateToDeletedUsers}
          >
            View Deleted Users
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/admin')}
            sx={{ mb: 2 }}
          >
            ← Back to Admin Dashboard
          </Button>
        </Box>
        
        {/* Users Table */}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Email Address</TableCell>
              <TableCell>Admin</TableCell>
              <TableCell>Disabled</TableCell>
              <TableCell>Email Verified</TableCell>
              <TableCell>Trust Level</TableCell>
              <TableCell>Can Moderate</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u._id}>
                <TableCell>{u.username}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <Switch
                    checked={u.isAdmin}
                    onChange={(e) => handleRoleChange(u._id, e.target.checked)}
                  />
                </TableCell>
                <TableCell>
                  <Switch
                    checked={u.isDisabled}
                    onChange={(e) => handleDisableToggle(u._id, e.target.checked)}
                  />
                </TableCell>
                <TableCell>
                  <Switch
                    checked={u.emailVerified || false}
                    onChange={(e) => handleEmailVerifiedToggle(u._id, e.target.checked)}
                  />
                </TableCell>
                <TableCell>{u.trustLevel ?? 0}</TableCell>
                <TableCell>
                  <Switch
                    checked={u.canModerate || false}
                    onChange={(e) => handleCanModerateToggle(u._id, e.target.checked)}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title="Delete User">
                    <IconButton onClick={() => handleDeleteUser(u._id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </>
  );
}

export default UserManagement;
